import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/throw';

import { AuthHttpClient } from '../../../../providers/auth-http.service';

import { Invoice } from '../../billing/invoice/store/invoice';
import { CreditCard } from '../../billing/credit-card/store/credit-card';

import { default as swal } from 'sweetalert2';
import { Package } from '../../billing/upgrade-or-downgrade/store/package';
import { CreditStats } from '../../billing/usage/store/credit-stats';
import { Bill } from '../../billing/subscription/store/bill';
import { OnetimeOrder } from '../../billing/one-time-credits/OnetimeOrder';


export class SharedClientService {

	public baseUrl: string = '/v1/client';
	public base2Url: string = '/v2/client';
	public emitEventActionSource = new Subject <any>();
	public emitEventAction$ = this.emitEventActionSource.asObservable();

	constructor(private sHttp: AuthHttpClient) {}

	public get(id: number): Observable<any> {
		let url = this.baseUrl + '/auth/client/' + id;
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public postClient(clientId: string, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.baseUrl + '/auth/client/' + clientId;
		return this.sHttp.post(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getOwner(id: number): Observable<any> {
		let url = this.base2Url + '/auth/client/' + id + '/owner';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getClientBillingCardDetails(clientId: number): Observable<CreditCard> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/payment/card';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public downgradeClientBillingPackage(clientId: string, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/package/downgrade';
		return this.sHttp.post(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public upgradeClientBillingPackage(clientId: string, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/package/upgrade';
		return this.sHttp.post(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public orderClientBillingOnetimeProduct(clientId: string, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/onetime/products/order';
		return this.sHttp.post(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public cancelClientBillingOnetimeProductOrder(clientId: string, orderId: string): Observable<any> {
		let body = JSON.stringify({});
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/onetime/products/order/' + orderId + '/cancel';
		return this.sHttp.post(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getClientBillingOnetimeProductOrders(clientId: string): Observable<OnetimeOrder[]> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/onetime/products/orders';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getClientBillingOnetimeProducts(clientId: string): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/onetime/products';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getClientBillingPackages(clientId: string): Observable<Package[]> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/packages';
		return this.sHttp.get(url)
			.map(this.extractData)
			.map((packagesMap) => {
				return Object.keys(packagesMap).map((identifier) => {
					return {
						...packagesMap[identifier],
						template_products: Object.keys(packagesMap[identifier].template_products).map((templateIdentifier) => {
							return {
								...packagesMap[identifier].template_products[templateIdentifier],
								plan: {
									...packagesMap[identifier].template_products[templateIdentifier].plan,
									credits: Object.keys(packagesMap[identifier].template_products[templateIdentifier].plan.credits).map((creditIdentifier) => packagesMap[identifier].template_products[templateIdentifier].plan.credits[creditIdentifier]),
									selected_credit_id: packagesMap[identifier].template_products[templateIdentifier].plan.credits[Object.keys(packagesMap[identifier].template_products[templateIdentifier].plan.credits)[0]].id
								}
							};
						})
					};
				}).sort((a, b) => a.importance - b.importance);
			})
			.catch(this.handleError);
	}

	public getClientBillingPaymentSetupIntent(clientId: number): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/payment/setup_intent';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getClientBillingPackageTemplate(clientId: string, packageTemplateId): Observable<Package> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/package_template/' + packageTemplateId;
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public updateClientStripePaymentMethodId(clientId: number, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/payment/card';
		return this.sHttp.patch(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getOwnerMessageStats(candidateId: string): Observable<any> {
		let url = this.base2Url + '/auth/client/' + candidateId + '/owner/message/stats';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public sendOwnerMessage(clientId: string, data: any): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/owner/message';
		let body = JSON.stringify(data);
		return this.sHttp.post(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getAccountOptions(clientId: string): Observable<any> {
		let url = this.baseUrl + '/auth/client/' + clientId + '/options';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public patchClientPassword(clientId: string, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.baseUrl + '/auth/client/' + clientId + '/password';
		return this.sHttp.patch(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public patchMfaPhoneActivation(clientId: string, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.baseUrl + '/auth/client/' + clientId + '/mfa_phone_activation';
		return this.sHttp.patch(url, body)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 400 || error.status === 401) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public patchLastSeen(clientId: string) {
		let body = '';
		let url = this.baseUrl + '/auth/client/' + clientId  + '/last_seen';
		return this.sHttp.patch(url, body)
			.map(this.extractData)
			.catch((err) => Observable.throw(err));
	}

	public getPingRateSeconds(clientId: string) {
		let url = this.baseUrl + '/auth/client/' + clientId  + '/ping-rate-seconds';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((err) => Observable.throw(err));
	}

	public putAccountOptions(clientId: string, data: any): Observable<any> {
		let body = JSON.stringify(data);
		let url = this.baseUrl + '/auth/client/' + clientId + '/options';
		return this.sHttp.put(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getPermissions(clientId: string): Observable<any> {
		let url = this.baseUrl + '/auth/client/' + clientId + '/permissions';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public updateStatus(clientId: string, data: any): Observable<any> {
		let url = this.baseUrl + '/auth/client/' + clientId + '/status';
		let body = JSON.stringify(data);
		return this.sHttp.patch(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getStripeCustomer(clientId: string): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/customer';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public cancelAccount(clientId: string): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/customer';
		return this.sHttp.delete(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public updateCustomerBillingInfo(clientId: string, data: any): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/customer/update_billing_information';
		let body: any = JSON.stringify(data);
		return this.sHttp.patch(url, body)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getClientOptions(clientId: string): Observable<any> {
		let url = this.baseUrl + '/auth/client/' + clientId + '/options';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch(this.handleError);
	}

	public getLatestBillingState(clientId: string): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/latest';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public getBillingInvoices(clientId: string): Observable<Invoice[]> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/invoices';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public getBillingCreditStats(clientId: string): Observable<CreditStats[]> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/credit/stats';
		return this.sHttp.get(url)
			.map(this.extractData)
			.map((statsObj: {string: CreditStats}) => Object.keys(statsObj).map((identifier) => {
				return {
					...statsObj[identifier],
					product_name: statsObj[identifier].product_identifier.split('-').join(' ')
				};
			}))
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public checkProductTemplateCreditAvailability(clientId: string, productTemplate: string): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/credit/product/' + productTemplate + '/availability';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public getCurrentBilling(clientId: string, queryParams?: any): Observable<Bill> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/current';
		return this.sHttp.get(url, queryParams)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public getUpcomingBilling(clientId: string): Observable<any> {
		let url = this.base2Url + '/auth/client/' + clientId + '/billing/upcoming';
		return this.sHttp.get(url)
			.map(this.extractData)
			.catch((error: any) => {
				if (error.status === 404) {
					return Observable.throw(error);
				}
				this.handleError(error);
			});
	}

	public getPylonIdentityHash(): Observable<any> {
		let url = '/v3/client/auth/client/pylon/identity-hash';
		return this.sHttp.get(url)
			.map(this.extractDataV3)
			.catch((err) => Observable.throw(err));
	}

	public emitEventAction(eventAction: any) {
		this.emitEventActionSource.next(eventAction);
	}

	private extractData(res: Response) {
		if (res) {
			let resp: any = res;
			if (res.status === 401) {
				window.location.assign(process.env.LANDING_PAGES_URL);
			}
			if (resp._body && resp._body !== '') {
				let body = res.json();
				if (typeof Object.keys(body)[0] !== 'undefined') {
					return body[Object.keys(body)[0]];
				}
			}
			return {};
		}
	}

	private extractDataV3(res: Response) {
		if (res) {
			if (res.status === 401) {
				window.location.assign(process.env.LANDING_PAGES_URL);
			}

			return res.json();
		}
	}

	private handleError(error: any) {
		let errMsg = (error.message) ? error.message :
		error.status ? `${error.status} - ${error.statusText}` : 'Server error';
		console.error(errMsg);
		swal({
			title: `Oops, there's been an error`,
			text: `Our tech team has been notified, however please email contact@hackajob.co and we'll try and solve the issue as soon as possible. Thanks and sorry for any inconvenience.`,
			type: 'error',
			showCancelButton: false,
			confirmButtonColor: '#008fb3',
			confirmButtonText: 'Ok',
			allowOutsideClick: false,
			allowEscapeKey: false
		});
		return Observable.throw(errMsg);
	}
static ctorParameters() { return [ { type: AuthHttpClient } ]; }}



// WEBPACK FOOTER //
// ./node_modules/ngc-webpack??ref--0-2!./node_modules/angular2-template-loader!./src/app/components/client/shared/services/shared-client.service.ts